import { Form, Formik } from 'formik';
import { FC } from 'react';
import { CombinedError, useMutation, useQuery } from 'urql';
import { showErrorToast } from '../../utils/showErrorToast';
import { showSuccessToast } from '../../utils/showSuccessToast';
import { useTranslation } from 'react-i18next';
import {
  AddIcon,
  Button,
  Checkbox,
  DatePicker,
  Grid,
  GridColumn,
  GridRow,
  InplaceEdit,
  MinusIcon,
} from '@bp/ui-components';
import { TeachingUnitSelect } from '../TeachingUnitSelect/TeachingUnitSelect';
import { BpCard } from '../BpCard/BpCard';
import { niceDate } from '../../utils/dateCalculations';
import { FileUpload } from '../FileUpload/FileUpload';
import styles from './WorkMaterialsForm.module.scss';
import { type FileEntryTableType, FileTable, FileTableFieldsDocument } from '../FileTable/FileTable';
import { courseSchema } from './validation/schema';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { BpTipTap } from '../BpTipTap/BpTipTap';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { useDays } from '../../hooks/useDays';
import { connectByUuid, connectDay, fileEntriesConnect } from '../../utils/connectLib';
import { getFragmentData, graphql } from 'client/gql';
import { useParams } from 'react-router-dom';
import { backendApi } from '../../utils/backendApi';
import { NotificationReason } from '../../utils/matrixClient';
import { BpCreateWorkMaterialsMutation } from '../../client/gql/graphql';

const CreateWorkMaterialsMutationDocument = graphql(`
  mutation BpCreateWorkMaterials($input: [WorkMaterialCreateInput!]!) {
    createWorkMaterials(input: $input) {
      workMaterials {
        uuid
        __typename
        holder {
          ... on TeachingUnit {
            uuid
            group {
              __typename
              uuid
              name
            }
          }
        }
      }
      info {
        nodesCreated
      }
    }
  }
`);
const UpdateWorkMaterialsMutationDocument = graphql(`
  mutation BpUpdateWorkMaterials($update: WorkMaterialUpdateInput!, $where: WorkMaterialWhere!) {
    updateWorkMaterials(update: $update, where: $where) {
      workMaterials {
        uuid
        __typename
        material {
          uuid
          __typename
          text
          fileEntries {
            uuid
            __typename
          }
        }
        publicFrom {
          uuid
          __typename
          date
        }
        title
        visibleAfterVisibleTo
        visibleFrom {
          uuid
          __typename
          date
        }
        visibleTo {
          uuid
          __typename
          date
        }
        holder {
          ... on TeachingUnit {
            uuid
            __typename
            group {
              __typename
              uuid
              name
            }
          }
        }
      }
    }
  }
`);

const CourseWorkMaterialsFormPageQueryDocument = graphql(`
  query CourseWorkMaterialsFormPageData($workMaterialUuid: ID!, $courseUuid: ID!) {
    workMaterials(where: { uuid: $workMaterialUuid }) {
      uuid
      __typename
      title
      material {
        uuid
        __typename
        text
        fileEntries {
          uuid
          __typename
          ...FileTableFields
        }
      }
      visibleFrom {
        uuid
        __typename
        date
      }
      visibleTo {
        uuid
        __typename
        date
      }
      publicFrom {
        uuid
        __typename
        date
      }
      visibleAfterVisibleTo
    }
    ...TeachingUnitSelectFields
  }
`);

export type WorkMaterialValuesType = {
  title: string;
  description: string;
  relevantFrom: Date;
  relevantTo: Date;
  visibleFrom: Date;
  teachingUnitUuid: string;
  visibleAfterVisibleTo: boolean;
  fileEntries: FileEntryTableType[];
  showPublicationDate: boolean;
};

export type WorkMaterialFormProps = {
  workMaterialUuid?: string;
  currentTeachingUnitUuid: string;
  isModal?: boolean;
  isDesktop?: boolean;
  onClose: (uuid?: string, title?: string) => void;
};

export const CourseWorkMaterialsForm: FC<WorkMaterialFormProps> = ({
  workMaterialUuid,
  currentTeachingUnitUuid,
  isModal = false,
  isDesktop = false,
  onClose,
}) => {
  const { t } = useTranslation();
  const { today } = useDays();
  const { courseUuid } = useParams();

  const pimAuthClaims = useAuthClaims().pimAuthClaims;
  const [, bpCreateWorkMaterial] = useMutation(CreateWorkMaterialsMutationDocument);
  const [, bpUpdateWorkMaterial] = useMutation(UpdateWorkMaterialsMutationDocument);

  const [{ data }] = useQuery({
    query: CourseWorkMaterialsFormPageQueryDocument,
    variables: { workMaterialUuid: workMaterialUuid ?? '', courseUuid: courseUuid! },
    pause: courseUuid === undefined,
  });

  const workMaterials = data?.workMaterials;

  const workMaterialToEdit = workMaterials?.find((mat) => mat.uuid === workMaterialUuid);

  const handleSubmit = async (values: WorkMaterialValuesType) => {
    let err: CombinedError | undefined;
    let newWorkmaterial:
      | Pick<BpCreateWorkMaterialsMutation, 'createWorkMaterials'>['createWorkMaterials']['workMaterials'][0]
      | undefined;
    const { description, title, visibleAfterVisibleTo, teachingUnitUuid, visibleFrom, relevantTo, relevantFrom } =
      values;

    const visibleFromDate = dayjs(visibleFrom).toISOString();
    const relevantToDate = dayjs(relevantTo).toISOString();
    const relevantFromDate = dayjs(relevantFrom).toISOString();

    if (workMaterialToEdit) {
      const { error, data } = await bpUpdateWorkMaterial({
        where: { uuid: workMaterialUuid },
        update: {
          material: {
            delete: {},
            create: {
              node: {
                text: description,
                textMediaType: 'text/plain',
                fileEntries: fileEntriesConnect(values.fileEntries),
              },
            },
          },
          publicFrom: {
            disconnect: {},
            ...connectDay(visibleFromDate),
          },
          title: title,
          visibleAfterVisibleTo: visibleAfterVisibleTo,
          visibleFrom: {
            disconnect: {},
            ...connectDay(relevantFromDate),
          },
          visibleTo: {
            disconnect: {},
            ...connectDay(relevantToDate),
          },
          holder: {
            TeachingUnit: {
              disconnect: {},
              ...connectByUuid(teachingUnitUuid),
            },
          },
        },
      });
      newWorkmaterial = data?.updateWorkMaterials.workMaterials[0];

      err = error;
    } else {
      const input = {
        input: {
          title: title,
          visibleFrom: connectDay(relevantFromDate),
          visibleTo: connectDay(relevantToDate),
          visibleAfterVisibleTo: visibleAfterVisibleTo,
          publicFrom: connectDay(visibleFromDate),
          owner: connectByUuid(pimAuthClaims.getProfile().uuid),
          material: {
            create: {
              node: {
                text: description,
                textMediaType: 'text/plain',
                fileEntries: fileEntriesConnect(values.fileEntries),
              },
            },
          },
          holder: {
            TeachingUnit: connectByUuid(teachingUnitUuid),
          },
        },
      };

      const { error, data } = await bpCreateWorkMaterial(input);
      newWorkmaterial = data?.createWorkMaterials.workMaterials[0];
      err = error;
    }

    if (err) showErrorToast(err);
    if (!err) {
      void backendApi.notify({
        type: workMaterialToEdit ? NotificationReason.CourseMaterialUpdated : NotificationReason.CourseMaterialCreated,
        groupUuid: courseUuid,
        groupName: newWorkmaterial?.holder.group.name,
        subjectUuid: workMaterialUuid ?? newWorkmaterial?.uuid ?? '',
        subjectName: values.title,
      });
      showSuccessToast(t('common.saved'));
    }
    onClose(workMaterialUuid, values.title);
  };

  const todayDay = dayjs(today?.date);
  const initialValues: WorkMaterialValuesType = {
    title: workMaterialToEdit?.title ?? '',
    description: workMaterialToEdit?.material.text ?? '',
    relevantFrom: workMaterialToEdit?.visibleFrom ? new Date(workMaterialToEdit?.visibleFrom.date) : todayDay.toDate(),
    relevantTo: workMaterialToEdit?.visibleTo
      ? new Date(workMaterialToEdit?.visibleTo.date)
      : todayDay.add(1, 'day').toDate(),
    visibleFrom: workMaterialToEdit?.publicFrom?.date
      ? new Date(workMaterialToEdit?.publicFrom?.date)
      : todayDay.toDate(),
    visibleAfterVisibleTo: workMaterialToEdit?.visibleAfterVisibleTo ?? false,
    teachingUnitUuid: currentTeachingUnitUuid,
    showPublicationDate: !!workMaterialToEdit?.publicFrom?.date,
    fileEntries: getFragmentData(FileTableFieldsDocument, workMaterialToEdit?.material.fileEntries ?? []),
  };

  return (
    <div className={styles['course-workmaterials-form']}>
      <Formik<WorkMaterialValuesType>
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={courseSchema}
      >
        {({ resetForm, setFieldValue, setFieldTouched, handleChange, values, isSubmitting, errors }) => {
          return (
            <Form className={classNames('bp__form', { 'is-modal': isModal })}>
              <div className={isModal ? 'bp__modal-header' : 'bp__form-header'}>
                <InplaceEdit
                  className={styles.title}
                  name='title'
                  size='xxl'
                  onBlur={async (value) => {
                    await setFieldValue('title', value);
                    await setFieldTouched('title', true);
                  }}
                  onChange={async (value) => {
                    await setFieldValue('title', value);
                    await setFieldTouched('title', true);
                  }}
                  defaultValue={values.title}
                  placeholder={t('common.addTitle')}
                  error={errors.title}
                  dense
                  backgroundColor={isModal ? 'white' : 'background'}
                />
                <div className={'bp__form-buttons'}>
                  <Button hierarchy='tertiary' type={'submit'} disabled>
                    {t('templates.useAsTemplate')}
                  </Button>
                  <Button
                    hierarchy='secondary'
                    onClick={() => {
                      resetForm();
                      onClose();
                    }}
                  >
                    {workMaterialUuid ? t('common.discardChanges') : t('common.cancel')}
                  </Button>
                  <Button hierarchy='primary' type={'submit'} disabled={!!errors.title} isLoading={isSubmitting}>
                    {t('common.save')}
                  </Button>
                </div>
              </div>

              <Grid className={!isDesktop ? 'mobile-margin' : undefined}>
                <GridRow mobileGap='var(--grid-column-gap)'>
                  <GridColumn width={8}>
                    <BpCard isEmbedded={isModal} header={{ headline: t('common.description') }}>
                      <BpTipTap
                        defaultValue={values.description}
                        onChange={async (value) => {
                          await setFieldValue('description', value);
                        }}
                        name={'description'}
                        required={false}
                        label={t('common.description')}
                        placeholder={t('common.description')}
                        error={errors.description}
                      />
                      <FileTable
                        className={styles['file-list']}
                        mode={'edit'}
                        files={values.fileEntries}
                        onRenamed={async (uuid, newName) => {
                          const updated = values.fileEntries.map((fileEntry) => {
                            if (fileEntry.uuid === uuid) {
                              return { ...fileEntry, filename: newName };
                            }
                            return fileEntry;
                          });
                          await setFieldValue('fileEntries', updated);
                        }}
                        onDeleted={async (uuid) => {
                          await setFieldValue(
                            'fileEntries',
                            values.fileEntries.filter((fileEntry) => {
                              return fileEntry.uuid !== uuid;
                            }),
                          );
                        }}
                        isGroupEditor
                        fullHeight
                      />
                      <FileUpload
                        onFileUpload={async (file) => {
                          await setFieldValue('fileEntries', [...values.fileEntries, file]);
                        }}
                      />
                    </BpCard>
                  </GridColumn>
                  <GridColumn width={4}>
                    <TeachingUnitSelect
                      className={!isModal ? 'mb-7' : undefined}
                      teachingUnitUuid={values.teachingUnitUuid}
                      onChange={async (teachingUnitUuid) => {
                        await setFieldValue('teachingUnitUuid', teachingUnitUuid);
                      }}
                      error={errors.teachingUnitUuid}
                      isModal={isModal}
                      query={data}
                    />
                    <BpCard isEmbedded={isModal} header={{ headline: t('common.data') }}>
                      <div className={styles['dates']}>
                        <div>
                          <DatePicker
                            className='mb-2'
                            name={'relevantFrom'}
                            dateFormat='dd.MM.yyyy'
                            label={t('workmaterials.relevantFrom')}
                            onChange={async (date) => {
                              await setFieldValue('relevantFrom', date);
                              await setFieldTouched('relevantFrom');
                              if (dayjs(values.relevantTo).isBefore(date)) {
                                await setFieldValue('relevantTo', date);
                                await setFieldTouched('relevantTo');
                              }
                              if (dayjs(values.visibleFrom).isAfter(date)) {
                                await setFieldValue('visibleFrom', date);
                                await setFieldTouched('visibleFrom');
                              }
                            }}
                            value={values.relevantFrom}
                            showMonthYearDropdown
                          />
                          <DatePicker
                            className='mb-5'
                            name={'relevantTo'}
                            dateFormat='dd.MM.yyyy'
                            label={t('workmaterials.visibleTo')}
                            onChange={async (date) => {
                              if (dayjs(date).isBefore(values.relevantFrom)) {
                                await setFieldValue('relevantFrom', date);
                                await setFieldTouched('relevantFrom');
                              }
                              await setFieldValue('relevantTo', date);
                              await setFieldTouched('relevantTo');
                            }}
                            value={values.relevantTo}
                            showMonthYearDropdown
                          />
                        </div>

                        <div className={styles['public-from-hint']}>{t('workmaterials.publicFromHint')}</div>

                        <div className={styles['public-from-date']}>
                          {t('workmaterials.publicFromDate', { date: niceDate(values.relevantFrom, 'short') })}
                        </div>

                        <Button
                          className={styles['full-width-button']}
                          hierarchy='secondary'
                          onClick={async () => {
                            await setFieldValue('showPublicationDate', !values.showPublicationDate);
                          }}
                          icon={
                            values.showPublicationDate ? (
                              <MinusIcon className={'svg-icon small'} />
                            ) : (
                              <AddIcon className={'svg-icon small'} />
                            )
                          }
                        >
                          {t('workmaterials.divergentDate')}
                        </Button>

                        {values.showPublicationDate && (
                          <DatePicker
                            name={'visibleFrom'}
                            dateFormat='dd.MM.yyyy'
                            label={t('common.visibleFrom')}
                            onChange={async (date) => {
                              if (dayjs(date).isAfter(values.relevantFrom)) {
                                await setFieldValue('visibleFrom', values.relevantFrom);
                                await setFieldTouched('visibleFrom', true);
                              } else {
                                await setFieldValue('visibleFrom', date);
                                await setFieldTouched('visibleFrom', true);
                              }
                            }}
                            value={values.visibleFrom}
                            showMonthYearDropdown
                          />
                        )}

                        <Checkbox
                          className='mt-5'
                          label={t('common.visibleAfter') as string}
                          name='visibleAfterVisibleTo'
                          checked={values.visibleAfterVisibleTo ?? false}
                          onChange={handleChange}
                        />
                      </div>
                    </BpCard>
                  </GridColumn>
                </GridRow>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
